import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ReferenceLine, Cell, LabelList,
} from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtEur, STATUS_META } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const STATUS_COLOR = { ottimo: "#10B981", adeguato: "#F59E0B", critico: "#EF4444", nd: "#64748B" };

const EMPTY = { year: new Date().getFullYear() + 1, ebitda: 0, imposte: 0, quota_capitale: 0, interessi: 0 };

export function DscrTab({ companyId }) {
  const [rows, setRows] = useState([]);
  const [computed, setComputed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [growth, setGrowth] = useState(5);
  const [nYears, setNYears] = useState(3);
  const [suggesting, setSuggesting] = useState(false);

  useEffect(() => {
    api.get(`/companies/${companyId}/dscr`)
      .then((r) => { setRows(r.data.rows || []); setComputed(r.data.computed || []); })
      .finally(() => setLoading(false));
  }, [companyId]);

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/dscr/suggest`, { params: { growth, years: nYears } });
      setRows(data.rows || []);
      toast.success(`Piano precompilato da EBITDA ${data.base_year} (${fmtEur(data.base_ebitda)}) con crescita ${growth}%`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSuggesting(false);
    }
  };

  const update = (i, key, v) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [key]: v === "" ? "" : Number(v) } : r)));
  const addRow = () => setRows((prev) => [...prev, { ...EMPTY, year: (prev[prev.length - 1]?.year || new Date().getFullYear()) + 1 }]);
  const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const clean = rows.map((r) => ({
        year: Number(r.year) || 0, ebitda: Number(r.ebitda) || 0, imposte: Number(r.imposte) || 0,
        quota_capitale: Number(r.quota_capitale) || 0, interessi: Number(r.interessi) || 0,
      }));
      const { data } = await api.put(`/companies/${companyId}/dscr`, { rows: clean });
      setComputed(data.computed || []);
      toast.success("Piano DSCR salvato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento...</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-semibold text-lg tracking-tight flex items-center gap-2"><TrendingUp className="h-5 w-5 text-primary" /> DSCR Prospettico</h3>
          <p className="text-sm text-muted-foreground">Inserisci il piano previsionale: DSCR = (EBITDA atteso − Imposte) / (Quota Capitale + Interessi). Soglia adeguata &gt; 1,1.</p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground block">Crescita %</label>
            <Input type="number" className="w-20 h-8 font-mono" value={growth} onChange={(e) => setGrowth(Number(e.target.value))} data-testid="dscr-growth-input" />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground block">Anni</label>
            <Input type="number" className="w-16 h-8 font-mono" value={nYears} onChange={(e) => setNYears(Number(e.target.value))} data-testid="dscr-years-input" />
          </div>
          <Button variant="outline" size="sm" onClick={suggest} disabled={suggesting} data-testid="dscr-suggest-btn">
            {suggesting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <TrendingUp className="h-4 w-4 mr-1.5" />} Precompila da bilancio
          </Button>
          <Button variant="outline" size="sm" onClick={addRow} data-testid="dscr-add-row"><Plus className="h-4 w-4 mr-1.5" /> Anno</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="dscr-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva Piano
          </Button>
        </div>
      </div>

      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-border/60 bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 text-left">Esercizio</th>
              <th className="px-4 py-3 text-right">EBITDA atteso</th>
              <th className="px-4 py-3 text-right">Imposte</th>
              <th className="px-4 py-3 text-right">Quota Capitale</th>
              <th className="px-4 py-3 text-right">Interessi</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Nessun anno. Aggiungi il primo esercizio previsionale.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-border/40 last:border-0">
                <td className="px-4 py-2"><Input type="number" className="w-24 font-mono h-8" value={r.year} onChange={(e) => update(i, "year", e.target.value)} data-testid={`dscr-year-${i}`} /></td>
                <td className="px-4 py-2"><Input type="number" className="font-mono h-8 text-right" value={r.ebitda} onChange={(e) => update(i, "ebitda", e.target.value)} data-testid={`dscr-ebitda-${i}`} /></td>
                <td className="px-4 py-2"><Input type="number" className="font-mono h-8 text-right" value={r.imposte} onChange={(e) => update(i, "imposte", e.target.value)} data-testid={`dscr-imposte-${i}`} /></td>
                <td className="px-4 py-2"><Input type="number" className="font-mono h-8 text-right" value={r.quota_capitale} onChange={(e) => update(i, "quota_capitale", e.target.value)} data-testid={`dscr-quota-${i}`} /></td>
                <td className="px-4 py-2"><Input type="number" className="font-mono h-8 text-right" value={r.interessi} onChange={(e) => update(i, "interessi", e.target.value)} data-testid={`dscr-interessi-${i}`} /></td>
                <td className="px-4 py-2 text-right"><button onClick={() => removeRow(i)} className="text-muted-foreground hover:text-red-500" data-testid={`dscr-remove-${i}`}><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {computed.some((c) => c.dscr != null) && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-heading font-semibold text-sm">Andamento DSCR Previsionale</h4>
            <span className="text-[11px] font-mono text-muted-foreground flex items-center gap-1.5">
              <span className="inline-block w-4 h-0.5 bg-red-500" style={{ borderTop: "2px dashed #EF4444", background: "transparent" }} />
              Soglia 1,1
            </span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={computed.filter((c) => c.dscr != null)} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="year" tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "hsl(var(--muted-foreground))" }} width={40} />
                <RTooltip
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 12 }}
                  formatter={(v) => [`${Number(v).toFixed(2)}x`, "DSCR"]}
                />
                <ReferenceLine y={1.1} stroke="#EF4444" strokeDasharray="5 4" strokeWidth={1.5}
                  label={{ value: "1,1", position: "right", fill: "#EF4444", fontSize: 11, fontFamily: "JetBrains Mono" }} />
                <Bar dataKey="dscr" radius={[4, 4, 0, 0]} maxBarSize={70}>
                  <LabelList dataKey="dscr" position="top" formatter={(v) => `${Number(v).toFixed(2)}x`}
                    style={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "hsl(var(--foreground))" }} />
                  {computed.filter((c) => c.dscr != null).map((c, i) => (
                    <Cell key={i} fill={STATUS_COLOR[c.status] || STATUS_COLOR.nd} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {computed.length > 0 && (
        <div>
          <h4 className="font-heading font-semibold text-sm mb-3">Risultato DSCR</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {computed.map((c) => {
              const st = STATUS_META[c.status] || STATUS_META.nd;
              return (
                <Card key={c.year} className="p-4" data-testid={`dscr-result-${c.year}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-semibold">{c.year}</span>
                    <Badge variant="outline" className={`text-[10px] font-mono ${st.cls}`}>{st.label}</Badge>
                  </div>
                  <div className="font-mono font-bold text-3xl mt-2 tracking-tight">{c.dscr != null ? `${c.dscr.toFixed(2)}x` : "n.d."}</div>
                  <div className="mt-3 pt-3 border-t border-border/50 text-xs text-muted-foreground font-mono space-y-1">
                    <div className="flex justify-between"><span>Flusso disponibile</span><span>{fmtEur(c.flusso_disponibile)}</span></div>
                    <div className="flex justify-between"><span>Servizio debito</span><span>{fmtEur(c.servizio_debito)}</span></div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
